/**
 * AI-CHAT-INSIGHTS — auto-surface workshop-register suggestions from the live
 * Analyst conversation, suggest-then-confirm.
 *
 * After each completed assistant reply this debounces a structured extraction
 * pass over the recent transcript (via the shared `useAiDraft` plumbing) and
 * proposes candidate register items. Nothing is written until the user accepts
 * one — matching the codebase's "AI fills a draft, a human commits" convention
 * (see `ai-draft.ts` header and the transcript-driven `AiExtractionSection`).
 *
 * Accepted suggestions land in the matching store and are tagged `ai-extracted`
 * for auditability:
 *   - finding / opportunity / recommendation → Insight Library
 *   - decision / action / assumption → Decision Log
 *   - note → Facilitation notes
 *
 * Scope:
 *   - A workshop scope (`"WS1".."WS8"`) writes to that workshop's registers.
 *   - The Mission Control sentinel `"MC"` has no single register home, so the
 *     model assigns each insight a target workshop (parsed + validated).
 *
 * Backend note: extraction reuses the Analyst chat backend selector via
 * `useAiDraft` — `streamAgentReply` when `VITE_CHAT_BACKEND=agent` (production),
 * else browser-direct `streamXaesReply` when `VITE_ANTHROPIC_API_KEY` is set.
 * Without either transport we don't fire a pass (the keyless mock banner
 * yields no parseable insights).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import {
  FINDING_DOMAIN_DEFAULT,
  FINDING_SEVERITY_DEFAULT,
  nsTag,
  TAG_NS_OPPORTUNITY,
} from "@/lib/taxonomy";
import { useActions } from "@/stores/useActions";
import { useAssumptions } from "@/stores/useAssumptions";
import { useDecisions } from "@/stores/useDecisions";
import { useFacilitationNotes } from "@/stores/useFacilitationNotes";
import { useFindings } from "@/stores/useFindings";
import { useOpportunities } from "@/stores/useOpportunities";
import { useRecommendations } from "@/stores/useRecommendations";
import type { ChatScope } from "./context-builder";
import type { ChatMessage, ChatStatus } from "./use-chat";
import {
  buildConversationInsightPrompt,
  parseInsightCandidates,
  useAiDraft,
  type InsightCandidate,
} from "./ai-draft";

/** Audit tag stamped on every chat-derived insight (namespaced on the
 *  opportunity path so the Insight Library facets it with opportunity tags).
 *  Shared with the transcript-driven extraction surface. */
const AI_TAG = "ai-extracted";
/** Settle window after a reply completes before extracting — lets a quick
 *  follow-up turn supersede a stale pass and keeps cost off every keystroke. */
const DEBOUNCE_MS = 1200;

/** A proposed insight plus a stable id for list reconciliation. */
export type SuggestedInsight = InsightCandidate & { id: string };

export type UseInsightSuggestionsArgs = {
  /** Conversation scope — a workshop id or the `"MC"` sentinel. */
  scope: ChatScope;
  /** Live chat messages (the hook reads completed turns only). */
  messages: ChatMessage[];
  /** Chat status — extraction waits for `"idle"` so it never races a stream. */
  status: ChatStatus;
  /** Display name stamped as `addedBy` on accepted records. */
  addedBy: string;
};

export type UseInsightSuggestionsResult = {
  /** Pending suggestions awaiting accept/dismiss. */
  suggestions: SuggestedInsight[];
  /** True while an extraction pass is streaming. */
  scanning: boolean;
  /** Whether a real transport is available (agent BFF or browser Anthropic key). */
  configured: boolean;
  /** Commit a suggestion to its register; returns the committed insight (for
   *  a toast) or `null` if the id is unknown / has no resolvable target. */
  accept: (id: string) => SuggestedInsight | null;
  /** Drop a suggestion without committing (won't be re-proposed this session). */
  dismiss: (id: string) => void;
  /** Drop every pending suggestion. */
  dismissAll: () => void;
};

/** Normalise a title for duplicate detection: lowercased, whitespace-collapsed. */
function normTitle(title: string): string {
  return title.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Render the completed turns as a plain transcript for the extractor,
 *  clipped from the tail so recent context survives the prompt budget. */
function buildTranscript(messages: ChatMessage[]): string {
  const lines: string[] = [];
  for (const m of messages) {
    if (m.streaming || m.error) continue;
    const text = m.text.trim();
    if (!text) continue;
    lines.push(`${m.role === "user" ? "User" : "XAES Analyst"}: ${text}`);
  }
  return lines.join("\n\n");
}

/** Workshops in scope for duplicate detection — the one workshop, or all of
 *  them for an MC conversation. */
function workshopsInScope(scope: ChatScope): Workshop["id"][] {
  return scope === "MC" ? WORKSHOPS.map((w) => w.id) : [scope];
}

/** Titles / statements / note bodies already present in the in-scope registers —
 *  read live from the stores so an accepted item isn't re-proposed. */
function existingTitles(scope: ChatScope): Set<string> {
  const set = new Set<string>();
  const findings = useFindings.getState().entriesByWorkshop;
  const opportunities = useOpportunities.getState().entriesByWorkshop;
  const recommendations = useRecommendations.getState().entriesByWorkshop;
  const decisions = useDecisions.getState().entriesByWorkshop;
  const actions = useActions.getState().entriesByWorkshop;
  const assumptions = useAssumptions.getState().entriesByWorkshop;
  const notes = useFacilitationNotes.getState().entriesByWorkshop;
  for (const wsId of workshopsInScope(scope)) {
    for (const f of findings[wsId] ?? []) set.add(normTitle(f.title));
    for (const o of opportunities[wsId] ?? []) set.add(normTitle(o.title));
    for (const r of recommendations[wsId] ?? []) set.add(normTitle(r.title));
    for (const d of decisions[wsId] ?? []) set.add(normTitle(d.title));
    for (const a of actions[wsId] ?? []) set.add(normTitle(a.title));
    for (const a of assumptions[wsId] ?? []) set.add(normTitle(a.statement));
    for (const n of notes[wsId] ?? []) set.add(normTitle(n.body));
  }
  return set;
}

/** Compose a facilitation-note body from title + optional detail. */
function noteBody(title: string, detail: string): string {
  const t = title.trim();
  const d = detail.trim();
  if (!d) return t;
  if (!t) return d;
  return `${t}\n\n${d}`;
}

export function useInsightSuggestions({
  scope,
  messages,
  status,
  addedBy,
}: UseInsightSuggestionsArgs): UseInsightSuggestionsResult {
  const ai = useAiDraft();
  const [suggestions, setSuggestions] = useState<SuggestedInsight[]>([]);

  // Titles we've already surfaced/accepted/dismissed this session — so a fresh
  // pass over the (now longer) transcript doesn't resurface the same insight.
  const seenRef = useRef<Set<string>>(new Set());
  // Id of the assistant reply we last scheduled a pass for — dedupes the effect
  // firing across the many re-renders a single reply triggers.
  const lastReplyIdRef = useRef<string | null>(null);
  const timerRef = useRef<number | null>(null);
  // Mirror of suggestions so accept/dismiss stay referentially stable.
  const suggestionsRef = useRef(suggestions);
  useEffect(() => {
    suggestionsRef.current = suggestions;
  }, [suggestions]);
  // Latest scope, so a pass that resolves after a scope switch can discard its
  // (now stale, wrong-register) results instead of appending them.
  const scopeRef = useRef(scope);
  useEffect(() => {
    scopeRef.current = scope;
  }, [scope]);
  // Latest `ai` handle for teardown — lets the unmount / scope-change cleanup
  // abort an in-flight pass without making those effects depend on `ai`.
  const aiRef = useRef(ai);
  useEffect(() => {
    aiRef.current = ai;
  }, [ai]);

  // Lazily hydrate the registers so duplicate detection sees prior entries and
  // BFF-backed writes merge cleanly. Idempotent (mirrors useHydrateRegisters).
  useEffect(() => {
    void useFindings.getState().hydrate();
    void useOpportunities.getState().hydrate();
    void useRecommendations.getState().hydrate();
    void useDecisions.getState().hydrate();
    void useActions.getState().hydrate();
    void useAssumptions.getState().hydrate();
    void useFacilitationNotes.getState().hydrate();
  }, []);

  // Reset all conversation-scoped state when the scope changes — the new scope
  // gets a clean tray and its own register home. Abort any in-flight pass so a
  // stale stream doesn't keep running against the old scope.
  useEffect(() => {
    setSuggestions([]);
    seenRef.current = new Set();
    lastReplyIdRef.current = null;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    aiRef.current.cancel();
  }, [scope]);

  // Tear down on unmount: clear any pending timer and abort an in-flight pass
  // so it can't settle (setState) after the panel is gone.
  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      aiRef.current.cancel();
    },
    [],
  );

  // The latest completed assistant reply — the trigger for a pass.
  const lastReply = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m && m.role === "assistant" && !m.streaming && !m.error && m.text.trim()) {
        return m;
      }
    }
    return null;
  }, [messages]);

  const runExtraction = useCallback(
    async (transcript: string): Promise<void> => {
      const passScope = scope;
      const { system, prompt } = buildConversationInsightPrompt(passScope, transcript);
      const draft = await ai.run(system, prompt);
      // The panel may have switched workshops while we streamed — drop a pass
      // whose results no longer belong to the visible scope.
      if (scopeRef.current !== passScope) return;
      const parsed = parseInsightCandidates(draft, {
        requireWorkshopId: passScope === "MC",
      });
      if (parsed.length === 0) return;

      const existing = existingTitles(scope);
      const batch = new Set<string>();
      const fresh: SuggestedInsight[] = [];
      for (const candidate of parsed) {
        const key = normTitle(candidate.title);
        if (seenRef.current.has(key) || existing.has(key) || batch.has(key)) {
          continue;
        }
        batch.add(key);
        seenRef.current.add(key);
        fresh.push({ ...candidate, id: crypto.randomUUID() });
      }
      if (fresh.length === 0) return;
      setSuggestions((prev) => [...prev, ...fresh]);
    },
    // Depend on `ai.run` (stable — it only changes with the model), not the
    // whole `ai` object (a fresh literal each render), so this callback and the
    // scheduling effect that lists it don't churn on every panel re-render.
    [scope, ai.run],
  );

  // Schedule a debounced pass when a new reply settles. Guarded so it fires
  // once per reply, never mid-stream, and only with a real key.
  useEffect(() => {
    if (!ai.configured) return;
    // A new turn started while a pass was pending — drop the stale schedule;
    // the next completed reply re-schedules over the fuller transcript.
    if (status !== "idle") {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    if (!lastReply) return;
    if (!messages.some((m) => m.role === "user" && !m.error && m.text.trim())) {
      return;
    }
    if (lastReplyIdRef.current === lastReply.id) return;
    lastReplyIdRef.current = lastReply.id;

    const transcript = buildTranscript(messages);
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void runExtraction(transcript);
    }, DEBOUNCE_MS);
  }, [ai.configured, status, lastReply, messages, runExtraction]);

  const accept = useCallback(
    (id: string): SuggestedInsight | null => {
      const insight = suggestionsRef.current.find((s) => s.id === id);
      if (!insight) return null;
      const wsId = scope === "MC" ? insight.workshopId : scope;
      if (!wsId) return null;
      const now = Date.now();
      const base = {
        id: crypto.randomUUID(),
        workshopId: wsId,
        createdAt: now,
        addedBy,
        tags: [AI_TAG] as string[],
      };

      switch (insight.kind) {
        case "opportunity":
          useOpportunities.getState().add(wsId, {
            ...base,
            title: insight.title,
            detail: insight.detail,
            status: "identified",
            owner: "",
            tags: [nsTag(TAG_NS_OPPORTUNITY, AI_TAG)],
          });
          break;
        case "recommendation":
          useRecommendations.getState().add(wsId, {
            ...base,
            title: insight.title,
            body: insight.detail,
            status: "draft",
            owner: "",
          });
          break;
        case "decision":
          useDecisions.getState().add(wsId, {
            ...base,
            title: insight.title,
            rationale: insight.detail,
            owner: "",
            status: "proposed",
          });
          break;
        case "action":
          useActions.getState().add(wsId, {
            ...base,
            title: insight.title,
            owner: "",
            due: "",
            status: "open",
          });
          break;
        case "assumption":
          useAssumptions.getState().add(wsId, {
            ...base,
            statement: insight.title,
            basis: insight.detail,
            owner: "",
            status: "assumed",
          });
          break;
        case "note":
          useFacilitationNotes.getState().add(wsId, {
            ...base,
            body: noteBody(insight.title, insight.detail),
          });
          break;
        case "finding":
          useFindings.getState().add(wsId, {
            ...base,
            title: insight.title,
            detail: insight.detail,
            severity: insight.severity ?? FINDING_SEVERITY_DEFAULT,
            status: "open",
            domain: insight.domain ?? FINDING_DOMAIN_DEFAULT,
            linkedWorkshopIds: [],
            owner: "",
          });
          break;
      }

      seenRef.current.add(normTitle(insight.title));
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
      return insight;
    },
    [scope, addedBy],
  );

  const dismiss = useCallback((id: string): void => {
    const insight = suggestionsRef.current.find((s) => s.id === id);
    if (insight) seenRef.current.add(normTitle(insight.title));
    setSuggestions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const dismissAll = useCallback((): void => {
    for (const s of suggestionsRef.current) seenRef.current.add(normTitle(s.title));
    setSuggestions([]);
  }, []);

  return {
    suggestions,
    scanning: ai.status === "streaming",
    configured: ai.configured,
    accept,
    dismiss,
    dismissAll,
  };
}
